import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import './MomentumRadar.css';
import MomentumSpike from './MomentumSpike';
import ThreePercentRulePage from './RuleStrategyPage';
import HighLowPage from './HighLowPage';
import ShortTermLOM from './ShortTermLOM';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const MomentumRadar = ({ onBack }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageView, setPageView] = useState("default");
  const [highLowData, setHighLowData] = useState(false);
  const [showShortTermLOM, setShowShortTermLOM] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [trend, setTrend] = useState('');
  const [direction, setDirection] = useState('');

  const safeStocks = Array.isArray(stocks) ? stocks : [];

  const checkDarkMode = () => {
    setIsDarkMode(document.documentElement.classList.contains('darkmode'));
  };

  useEffect(() => {
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const formatTime = (datetime) => {
    if (!datetime) return "";
    const d = new Date(datetime);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE_URL}/api/fno-momentum-gainers/5m/`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const jsonData = await response.json();
      setStocks(jsonData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pageView) return;
    fetchData();
  }, [pageView, fetchData]);

  if (pageView === "5min") {
    return <MomentumSpike onBack={() => setPageView("default")} />;
  }

  if (pageView === "3percent") {
    return <ThreePercentRulePage onBack={() => setPageView("default")} />;
  }

  if (highLowData) {
    return <HighLowPage onBack={() => setHighLowData(false)} />;
  }

  if (showShortTermLOM) {
    return <ShortTermLOM onBack={() => setShowShortTermLOM(false)} />;
  }

  return (
    <div className={`momentum-radar-container ${isDarkMode ? 'dark' : ''}`}>
      <header className="momentum-header">
        <div className="momentum-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="momentum-user">
            <RefreshButton 
              onRefresh={() => fetchData()} 
              isLoading={loading} 
              size="small"
              className="mr-2"
            />
            <button className="momentum-back-btn" onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>

      <main className="momentum-main">
        <div className="momentum-header-bar">
          <h1>Momentum Radar</h1>
          <div className="filter-buttons">
            <h3>Filter By:</h3>
            <button className="filter-btn active">F&O Stocks</button>
          </div>
        </div>

        <div className="spike-sections-container">
          <div className="spike-section">
            <div className="toggle-design-container">
              <button 
                className="toggle-design-btn"
                onClick={() => setShowShortTermLOM(true)}
              >
                LOSS OF MOMENTUM (LOM)
              </button>
              <div className="button-group">
                <span 
                  className="btn green-btn"
                  onClick={() => {
                    setTrend('high');
                    setShowShortTermLOM(true);
                  }}
                >
                  HIGH
                </span>
                <span 
                  className="btn red-btn"
                  onClick={() => {
                    setTrend('low');
                    setShowShortTermLOM(true);
                  }}
                >
                  LOW
                </span>
              </div>
            </div>
          </div>
          
          <div className="spike-section">
            <div className="toggle-design-container">
              <button 
                className="toggle-design-btn" 
                onClick={() => setHighLowData(true)}
              >
                Switch 2 DAY HIGH & LOW
              </button>
              <div className="button-group">
                <span className="btn green-btn" onClick={() => setDirection('UP')}>UP</span>
                <span className="btn red-btn" onClick={() => setDirection('DOWN')}>DOWN</span>
              </div>
            </div>
          </div>
          
          <div className="spike-section">
            <div className="toggle-design-container">
              <button 
                onClick={() => setPageView('5min')} 
                className="toggle-design-btn"
              >
                5 & 15 min Momentum Spike
              </button>
              <div className="button-group">
                <span className="btn green-btn" onClick={() => console.log('5min UP clicked')}>UP</span>
                <span className="btn red-btn" onClick={() => console.log('5min DOWN clicked')}>DOWN</span>
              </div>
            </div>
          </div>
          
          <div className="spike-section">
            <div className="toggle-design-container">
              <button 
                onClick={() => setPageView('3percent')} 
                className="toggle-design-btn"
              >
                3% Rule
              </button>
              <div className="button-group">
                <span className="btn green-btn" onClick={() => console.log('3% Rule UP clicked')}>UP</span>
                <span className="btn red-btn" onClick={() => console.log('3% Rule DOWN clicked')}>DOWN</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div className="stocks-grid">
            {safeStocks.map((stock, index) => (
              <div key={index} className="stock-card">
                <div className="stock-header">
                  <span className="stock-symbol">
                    <WatchlistStar 
                      stock={{
                        symbol: stock.symbol,
                        name: stock.symbol,
                        price: stock.price,
                        change: stock.change,
                        time: stock.time
                      }} 
                      size="16px"
                    />
                    {stock.symbol}
                  </span>
                  <span className="stock-price">{stock.price}</span>
                </div>
                <div className="stock-details">
                  <span className={`stock-change ${stock.change >= 0 ? 'positive' : 'negative'}`}>
                    {stock.change}%
                  </span>
                  <span className="stock-time">{formatTime(stock.time)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MomentumRadar;